export type AccountSection =
  | 'profile'
  | 'notifications'
  | 'embed-urls'
  | 'security'
  | 'workspaces';

export type WorkspaceSection =
  | 'general'
  | 'members'
  | 'billing'
  | 'credits'
  | 'sso'
  | 'enterprise'
  | 'projects'
  | 'pages'
  | 'api-credentials'
  | 'provider-keys';

export type AccountPageSection = AccountSection | WorkspaceSection;

export const ACCOUNT_HOME = '/account/profile';

export const accountPath = (section: AccountSection) => `/account/${section}`;

/**
 * Path to a workspace section, with an optional sub path for nested flows
 * (e.g. `workspacePath(id, 'billing', 'change')`).
 */
export const workspacePath = (
  workspaceId: number,
  section: WorkspaceSection = 'general',
  subPath?: string
) => {
  const base =
    section === 'general'
      ? `/account/workspaces/${workspaceId}`
      : `/account/workspaces/${workspaceId}/${section}`;
  return subPath ? `${base}/${subPath}` : base;
};

export const workspaceSectionLabels: Record<WorkspaceSection, string> = {
  general: 'General',
  members: 'Members',
  billing: 'Plan & billing',
  credits: 'Usage credits',
  sso: 'Single sign-on',
  enterprise: 'Enterprise controls',
  projects: 'Projects',
  pages: 'Hosted pages',
  'api-credentials': 'API credentials',
  'provider-keys': 'Provider keys',
};

export const accountSectionLabels: Record<AccountSection, string> = {
  profile: 'Profile',
  notifications: 'Notifications',
  'embed-urls': 'Embed URLs',
  security: 'Security',
  workspaces: 'Workspaces',
};

const LAST_WORKSPACE_KEY = 'rive:account:lastWorkspace';

export function readLastWorkspace(): number | null {
  try {
    const value = window.localStorage.getItem(LAST_WORKSPACE_KEY);
    const id = value ? parseInt(value) : NaN;
    return isNaN(id) ? null : id;
  } catch {
    return null;
  }
}

export function writeLastWorkspace(workspaceId: number) {
  try {
    window.localStorage.setItem(LAST_WORKSPACE_KEY, `${workspaceId}`);
  } catch {
    // Storage can be unavailable (private mode); remembering is best effort.
  }
}
